import type { ProgramCurriculumProductsQuery_ProgramCurriculumProductsV1Resource_multiGet_elements } from 'bundles/enterprise-learning-path-description-page/queries/__generated__/ProgramCurriculumProductsQuery';

type CurriculumProduct =
  NonNullable<ProgramCurriculumProductsQuery_ProgramCurriculumProductsV1Resource_multiGet_elements>;

/**
 * Finds the course ID of the most recently active enrolled course that is not completed.
 * Returns the course ID if found, otherwise returns null.
 */
export const findActiveEnrolledCourseId = (curriculumProducts: CurriculumProduct[]): string | null => {
  // Filter for courses that are enrolled and not completed
  const enrolledCourses = curriculumProducts.filter((product) => {
    if (product.productState.__typename !== 'ProgramCurriculumProductsV1_programCourseWithStateMember') {
      return false;
    }

    const courseState = product.productState.productState;
    // enrolledAt is a timestamp and can be 0 (which is valid in our case).
    const isEnrolled = courseState.enrolledAt !== undefined && courseState.enrolledAt !== null;
    const isNotCompleted = courseState.completedAt === undefined || courseState.completedAt === null;

    return isEnrolled && isNotCompleted;
  });

  if (enrolledCourses.length === 0) {
    return null;
  }

  // Sort by most recent activity (lastUpdatedAt or enrolledAt as fallback)
  const sortedCourses = enrolledCourses.sort((a, b) => {
    // Type assertion since we already filtered for course members above
    const aProductState = a.productState as Extract<
      typeof a.productState,
      { __typename: 'ProgramCurriculumProductsV1_programCourseWithStateMember' }
    >;
    const bProductState = b.productState as Extract<
      typeof b.productState,
      { __typename: 'ProgramCurriculumProductsV1_programCourseWithStateMember' }
    >;

    const aActivity = aProductState.productState.lastUpdatedAt || aProductState.productState.enrolledAt || 0;
    const bActivity = bProductState.productState.lastUpdatedAt || bProductState.productState.enrolledAt || 0;

    return Number(bActivity) - Number(aActivity); // Most recent first
  });

  // Return the course ID of the most recently active course
  const mostActiveCourse = sortedCourses[0];
  if (!mostActiveCourse) {
    return null;
  }

  const courseProductState = mostActiveCourse.productState as Extract<
    typeof mostActiveCourse.productState,
    { __typename: 'ProgramCurriculumProductsV1_programCourseWithStateMember' }
  >;
  return courseProductState.productState.courseId;
};
